import { createContext, useContext, useState, useEffect, type ReactNode } from "react";

// ---------- Tipos ----------
export type Rol = "admin" | "cotizador" | "contabilidad";

interface Usuario {
  id: string;
  nombre: string;
  email: string;
  rol: Rol;
}

interface AuthContextType {
  usuario: Usuario | null;
  login: (email: string, password: string, recordar?: boolean) => Promise<void>;
  logout: () => Promise<void>;
  error: string;
  cargando: boolean; // nuevo: para saber si se está validando sesión
}

// ---------- Constantes ----------
const TOKEN_KEY = "airmex_token";
const API_BASE =  "https://api.airmex.mx/api/v1";

// ---------- Helpers ----------
function getStoredToken(): { token: string; storage: Storage } | null {
  const local = localStorage.getItem(TOKEN_KEY);
  if (local) return { token: local, storage: localStorage };
  const session = sessionStorage.getItem(TOKEN_KEY);
  if (session) return { token: session, storage: sessionStorage };
  return null;
}

// ---------- Contexto ----------
const AuthContext = createContext<AuthContextType>({
  usuario: null,
  login: async () => {},
  logout: async () => {},
  error: "",
  cargando: true,
});

export function AuthProvider({ children }: { children: ReactNode }) {
  const [usuario, setUsuario] = useState<Usuario | null>(null);
  const [error, setError] = useState("");
  const [cargando, setCargando] = useState(true);

  // ---------- Verificar sesión al cargar ----------
  useEffect(() => {
  const verificarSesion = async () => {
    const stored = getStoredToken();
    if (!stored) {
      setCargando(false);
      return;
    }
    try {
      const res = await fetch(`${API_BASE}/auth/me`, {
        headers: { Authorization: `Bearer ${stored.token}` },
      });
      if (!res.ok) throw new Error("Sesión inválida");
      const userData: Usuario = await res.json();
      setUsuario(userData);
    } catch (err) {
      // Limpia token y estado si falla
      stored.storage.removeItem(TOKEN_KEY);
      setUsuario(null);
    } finally {
      setCargando(false);
    }
  };
  verificarSesion();
}, []);

  // ---------- Login ----------
  const login = async (email: string, password: string, recordar: boolean = false) => {
    setError("");
    const body = JSON.stringify({ email, password, recordar });

    let res: Response;
    try {
      res = await fetch(`${API_BASE}/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body,
      });
    } catch {
      setError("No se pudo conectar con el servidor.");
      return;
    }

    if (!res.ok) {
      const data = await res.json().catch(() => ({ detail: "Error desconocido" }));
      setError(data.detail || "Credenciales inválidas");
      return;
    }

    const data = await res.json();
    const { access_token, user } = data;

    // Guardar token según recordar
    if (recordar) {
      localStorage.setItem(TOKEN_KEY, access_token);
      sessionStorage.removeItem(TOKEN_KEY);
    } else {
      sessionStorage.setItem(TOKEN_KEY, access_token);
      localStorage.removeItem(TOKEN_KEY);
    }

    // Mapear el usuario a nuestro modelo (ajusta si cambian los campos)
    const usuarioLogueado: Usuario = {
      id: user.id,
      nombre: user.full_name || user.email,
      email: user.email,
      rol: user.rol || "admin", // temporal si no viene del backend
    };
    setUsuario(usuarioLogueado);
  };

  // ---------- Logout ----------
  const logout = async () => {
    const stored = getStoredToken();
    if (stored) {
      try {
        await fetch(`${API_BASE}/auth/logout`, {
          method: "POST",
          headers: { Authorization: `Bearer ${stored.token}` },
        });
      } catch {
        // ignorar errores de red, el front igual cierra sesión
      }
      stored.storage.removeItem(TOKEN_KEY);
    }
    setUsuario(null);
    setError("");
  };

  return (
    <AuthContext.Provider value={{ usuario, login, logout, error, cargando }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}